import React, { useCallback, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { toast } from 'react-toastify';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import useHorarios from '../../../hooks/useHorarios';
import api from '../../../services/api';

const GerarHorariosModal = ({
  show,
  onHide,
  idMedico,
  nomeMedico,
  dateAgendaFormatted,
  mutateAgendas,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const diaSemana = useMemo(() => {
    if (!dateAgendaFormatted) return null;
    return new Date(dateAgendaFormatted).getDay() + 1;
  }, [dateAgendaFormatted]);

  const { data: horarios = [], isLoading, error } = useHorarios(
    show,
    idMedico,
    diaSemana
  );

  const dataFormatada = useMemo(() => {
    if (!dateAgendaFormatted) return;
    return format(parseISO(dateAgendaFormatted), 'dd/MM/yyyy', { locale: pt });
  }, [dateAgendaFormatted]);


  const gerarHorarios = useCallback(async () => {
    if (isLoading || error) return;
    if (!idMedico) {
      return toast.warn('Selecione um médico para gerar horários!');
    }

    if (!horarios.length) {
      return toast.info('Médico não atende neste dia!');
    }

    try {
      setIsSubmitting(true);
      const horariosMap = horarios.map(h => ({
        ...h,
        DATAAGENDA: dateAgendaFormatted,
        OBS: h.OBS || '',
      }));
      const { data } = await api.post(`/agenda/horarios`, horariosMap);
      toast.success(
        data?.message || `${data?.inseridos ?? horariosMap.length} horários gerados com sucesso!`
      );
      await mutateAgendas();
      onHide();
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.error || 'Erro ao gerar horários!';
      if (status === 409) {
        toast.warn(message);
        return;
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    idMedico,
    dateAgendaFormatted,
    mutateAgendas,
    horarios,
    onHide,
    isLoading,
    error,
  ]);

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton className="font-weight-bold">
        Gerar horários?
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-3">
            <Spinner animation="border" role="status" aria-label="Carregando horários" />
          </div>
        ) : (
          <>
            {error && (
              <Alert variant="danger" className="mb-3">
                Falha ao buscar horários!
              </Alert>
            )}
            <p>
              <strong>Médico:</strong> {nomeMedico ?? idMedico}
            </p>
            <p>
              <strong>Data:</strong> {dataFormatada}
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="success"
          onClick={gerarHorarios}
          disabled={isLoading || isSubmitting || Boolean(error)}
          aria-label="Confirmar geração de horários"
        >
          {isSubmitting ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            'Confirmar'
          )}
        </Button>
      </Modal.Footer>
    </Modal >
  );
};

export default GerarHorariosModal;
